import React, {
  useCallback,
  useState,
} from 'react';
import classNames from 'classnames';
import Image from '$/components/NextImage';
import IconFont from '$/components/IconFont';
import Button from '$/components/base/Button';
import type { SuperProduct } from '$/api/server.types';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import useThemeStore from '$/store/useThemeStore';
import { getRoboneoPageUrl } from '$/utils/roboneoPageUrl';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../Dialog';
import TeamSeatSelector from '../TeamSeatSelector';
import { clampTeamSeatCount } from '../teamSeatLimit';
import zfbIcon from '../PayQrcodeModal/zfb.svg';
import styles from './index.module.scss';

export interface TwoConfirmTeamOrderProps {
  open?: boolean;
  defaultOpen?: boolean;
  qrcodeUrl?: string;
  product?: SuperProduct;
  seatCount?: number;
  teamName?: string;
  showCreateTeamTip?: boolean;
  planTitleText?: string;
  periodText?: string;
  validityText?: string;
  priceUnit?: string;
  minSeatCount?: number;
  maxSeatCount?: number;
  overlayZIndex?: number;
  paying?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  onSeatCountChange?: (seatCount: number) => void;
  onPay?: () => void | Promise<void>;
}

interface TeamNameFieldProps {
  label: string;
  teamName: string;
}

/** 独立渲染团队名称，席位调整时避免长名称区域跟随重复刷新。 */
const TeamNameField = React.memo(({
  label,
  teamName,
}: TeamNameFieldProps) => {
  return (
    <div className={styles.block}>
      <p className={styles.sectionLabel}>{label}</p>
      <div className={styles.teamNameText} title={teamName} aria-label={teamName}>
        <span className={styles.teamNameValue}>{teamName}</span>
      </div>
    </div>
  );
});

/** 从点击商品读取可参与席位合计的单价，优先消费按钮展示价。 */
function getProductUnitPrice(product?: SuperProduct) {
  const buttonPrice = Number(String(product?.buttonExplain?.price || '').replace(/[^\d.-]/g, ''));
  if (Number.isFinite(buttonPrice) && buttonPrice > 0) return buttonPrice;

  const priceInCent = Number(product?.productPrice?.price);
  return Number.isFinite(priceInCent) && priceInCent > 0 ? priceInCent / 100 : 0;
}

/** 从点击商品读取货币符号，保持确认单与套餐卡展示一致。 */
function getProductMoneySymbol(product?: SuperProduct) {
  return product?.buttonExplain?.moneySymbol || product?.productPrice?.moneySymbol || '';
}

/** 使用商品货币符号格式化动态金额。 */
function formatPrice(price: number, moneySymbol: string) {
  const formattedPrice = price.toLocaleString('zh-CN', {
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${moneySymbol}${formattedPrice}`;
}

/** 使用中文格式化权益数量，保持订单明细与设计稿中的数字分隔一致。 */
function formatAmount(amount: number) {
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** 替换 Smartcat 文案中的简单字符串占位符。 */
function fillTemplate(template: string, params: Record<string, string | number>) {
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

/** 移除语言包富文本标记，供按钮和摘要等纯文本区域复用同一份翻译。 */
function stripHtmlTags(template: string) {
  return template.replace(/<[^>]+>/g, '');
}

/** 从会员协议富文本中拆出普通说明与可点击协议名称。 */
function getAgreementTextParts(template: string) {
  const spanTextList = Array.from(template.matchAll(/<span[^>]*>(.*?)<\/span>/g))
    .map((match) => stripHtmlTags(match[1]));
  if (spanTextList.length >= 2) {
    return {
      prefix: spanTextList[0],
      linkText: spanTextList.slice(1).join(''),
    };
  }

  return {
    prefix: '',
    linkText: stripHtmlTags(template),
  };
}

/**
 * 二维码生成成功后渲染团队订单支付确认弹窗，展示字段缺失时不阻断支付流程。
 */
function TwoConfirmTeamOrder({
  open,
  defaultOpen = false,
  qrcodeUrl,
  product,
  seatCount: controlledSeatCount,
  teamName,
  showCreateTeamTip = false,
  planTitleText,
  periodText,
  validityText,
  priceUnit,
  minSeatCount,
  maxSeatCount,
  overlayZIndex,
  paying = false,
  onOpenChange,
  onClose,
  onSeatCountChange,
  onPay,
}: TwoConfirmTeamOrderProps) {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const currentCountryCode = useOfficialStore((state) => state.currentCountryCode);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const theme = useThemeStore((state) => state.theme);
  const isControlled = typeof open === 'boolean';
  const actualOpen = isControlled ? open : innerOpen;

  /** 同步受控与非受控状态，并在关闭时通知购买流程。 */
  const updateOpen = useCallback((nextOpen: boolean) => {
    if (!isControlled) setInnerOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  }, [isControlled, onClose, onOpenChange]);

  /** 更新团队席位数并同步订单金额与权益数量。 */
  const handleSeatCountChange = useCallback((nextCount: number) => {
    if (minSeatCount === undefined) return;
    const safeCount = clampTeamSeatCount({
      minCount: minSeatCount,
      maxCount: maxSeatCount,
    }, nextCount);
    onSeatCountChange?.(safeCount);
  }, [maxSeatCount, minSeatCount, onSeatCountChange]);

  if (!product
    || controlledSeatCount === undefined
    || minSeatCount === undefined
    || (!isOversea && !qrcodeUrl)) {
    return null;
  }

  const translate = languageStore.smartcatTranslationText;
  const copy = {
    close: translate((v) => v.close),
    title: translate((v) => v.confirm_team_order),
    orderConfiguration: translate((v) => v.order_configuration),
    changePlan: translate((v) => v.change_plan),
    teamName: translate((v) => v.team_name),
    purchaseSeat: translate((v) => v.purchase_seat),
    total: translate((v) => v.total),
    orderDetails: translate((v) => v.order_details),
    validity: translate((v) => v.validity),
    seatCount: translate((v) => v.number_of_seats),
    singlePoints: translate((v) => v.single_event_integration),
    paymentAmount: translate((v) => v.payment_amount),
    benefitsTitle: translate((v) => v.team_rights_and_interests_attribution_statement),
    benefitsContent: translate(
      (v) => v.points_positions_and_assets_are_all_owned_by_the_team_asset_space_exclusive_package_tier_benefits_are_not_stacked_based_on_the_number_of_seats_,
    ),
  };
  const seatCount = controlledSeatCount;
  const unitPrice = getProductUnitPrice(product);
  const moneySymbol = getProductMoneySymbol(product);
  const shownPeriodText = periodText ?? product.subPeriodDesc ?? product.sub_period_desc ?? '';
  const shownValidityText = validityText ?? shownPeriodText;
  const shownPriceUnit = priceUnit ?? product.productPriceExplain ?? product.product_price_explain ?? '';
  const productTitleContent = product.productTitleContent || product.product_title_content;
  const powerText = productTitleContent?.completeTitle || productTitleContent?.complete_title || '';
  const totalPrice = unitPrice * seatCount;
  const shownQrcodeUrl = qrcodeUrl;
  const shownTeamName = teamName?.trim() || '';
  const promotionDisplay = product.productPromotionDisplay || product.product_promotion_display;
  const promotionPriceText = promotionDisplay?.promotionPriceText || promotionDisplay?.promotion_price_text || '';
  const formattedUnitPrice = formatPrice(unitPrice, moneySymbol);
  const formattedTotalPrice = formatPrice(totalPrice, moneySymbol);
  const formattedUnitPriceNumber = formatAmount(unitPrice);
  const formattedTotalPriceNumber = formatAmount(totalPrice);
  const seatText = fillTemplate(translate((v) => v._num_seats), { num: seatCount });
  const shownPlanTitleText = planTitleText
    ?? [product.productName, shownPeriodText].filter(Boolean).join(' · ');
  const planMetaText = [shownPeriodText, seatText].filter(Boolean).join(' · ');
  const createTeamTip = translate(
    (v) => v.upon_successful_payment_a_team_will_be_created_and_2_flagship_membership_seats_will_be_activated_,
  ).replace(/\b2\b/, String(seatCount));
  const calculationText = fillTemplate(
    stripHtmlTags(translate((v) => v.calculated_as_currency_sign_price_seat_year_num_seats)),
    {
      currency_sign: moneySymbol,
      price: formattedUnitPriceNumber,
      num: seatCount,
    },
  );
  const agreementText = getAgreementTextParts(
    translate((v) => v.subscription_implies_agreement_to_the_roboneo_membership_agreement_),
  );
  const payButtonText = fillTemplate(
    stripHtmlTags(translate((v) => v.confirm_and_pay_currency_symbol_price_)),
    {
      currency_symbol: moneySymbol,
      price: formattedTotalPriceNumber,
    },
  );
  const membershipPayAgreementUrl = getRoboneoPageUrl('/membership-pay-agreement', {
    countryCode: currentCountryCode,
    theme,
  });

  return (
    <Dialog
      open={actualOpen}
      onOpenChange={updateOpen}
      closeOnOverlayClick={false}
    >
      <DialogContent
        autoHeight
        closeLabel={copy.close}
        closeIcon={<IconFont name="crossBlack" fontSize={16} color="currentColor" />}
        overlayZIndex={overlayZIndex}
        overlayClassName={styles.overlay}
        containerClassName={classNames(styles.dialogContainer, {
          [styles.overseaDialogContainer]: isOversea,
        })}
        className={classNames(styles.dialogContent, {
          [styles.overseaDialogContent]: isOversea,
        })}
      >
        <section className={classNames(styles.orderArea, {
          [styles.overseaOrderArea]: isOversea,
        })}
        >
          <DialogTitle className={styles.title}>{copy.title}</DialogTitle>

          <div className={styles.block}>
            <p className={styles.sectionLabel}>{copy.orderConfiguration}</p>
            <div className={styles.planCard}>
              <div className={styles.planRow}>
                <div className={styles.planTitleWrap}>
                  <strong>
                    {shownPlanTitleText}
                  </strong>
                  <button
                    type="button"
                    className={styles.changePlan}
                    onClick={() => updateOpen(false)}
                  >
                    {copy.changePlan}
                  </button>
                </div>
                <strong className={styles.unitPrice}>
                  {formattedUnitPrice}
                  {shownPriceUnit}
                </strong>
              </div>
              <div className={styles.planMeta}>
                <span>{planMetaText}</span>
                <span>{promotionPriceText}</span>
              </div>
            </div>
          </div>

          <div className={styles.formFields}>
            <TeamNameField label={copy.teamName} teamName={shownTeamName} />
            <div className={styles.block}>
              <p className={styles.sectionLabel}>{copy.purchaseSeat}</p>
              <TeamSeatSelector
                value={seatCount}
                min={minSeatCount}
                max={maxSeatCount}
                seatText={seatText}
                totalLabel={copy.total}
                totalText={formattedTotalPrice}
                variant="order"
                decreaseLabel={isOversea ? 'Decrease seats' : undefined}
                increaseLabel={isOversea ? 'Increase seats' : undefined}
                onChange={handleSeatCountChange}
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.block}>
            <p className={styles.sectionLabel}>{copy.orderDetails}</p>
            <dl className={styles.orderDetails}>
              {shownValidityText ? (
                <div className={styles.detailRow}>
                  <dt>{copy.validity}</dt>
                  <dd>{shownValidityText}</dd>
                </div>
              ) : null}
              <div className={styles.detailRow}>
                <dt>{copy.seatCount}</dt>
                <dd>{seatText}</dd>
              </div>
              {powerText ? (
                <div className={styles.detailRow}>
                  <dt>{copy.singlePoints}</dt>
                  <dd>{powerText}</dd>
                </div>
              ) : null}
            </dl>
          </div>

          <div className={styles.divider} />

          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <strong className={styles.summaryLabel}>{copy.paymentAmount}</strong>
              <strong className={styles.summaryAmount}>{formattedTotalPrice}</strong>
            </div>
            <div className={styles.summaryMeta}>
              {showCreateTeamTip ? <span>{createTeamTip}</span> : null}
              <span>{calculationText}</span>
            </div>
          </div>
        </section>

        <section className={classNames(styles.paymentArea, {
          [styles.overseaPaymentArea]: isOversea,
        })}
        >
          <div className={styles.paymentContent}>
            <div className={styles.qrcodeStack}>
              <div className={styles.qrcodeBox}>
                {shownQrcodeUrl ? <img src={shownQrcodeUrl} alt="团队订单支付二维码" crossOrigin="anonymous" /> : null}
              </div>
              <div className={styles.scanGuide}>
                <div className={styles.paymentMethods}>
                  <span>
                    {languageStore.smartcatTranslationText((v) => v.please_scan_the_qr_code_to_complete_payment)}
                  </span>
                  <Image alt="" src={zfbIcon} width={18} height={18} style={{ marginLeft: '4px' }} />
                </div>
              </div>
            </div>

            <div className={styles.agreementBlock}>
              <p className={styles.agreement}>
                {agreementText.prefix}
                <a href={membershipPayAgreementUrl} target="_blank" rel="noreferrer">
                  {agreementText.linkText}
                </a>
              </p>
            </div>

            {isOversea ? (
              <Button
                type="default"
                size="large"
                className={styles.payButton}
                disabled={paying}
                onClick={onPay}
                buttonStyle={{ width: '280px' }}
              >
                {payButtonText}
              </Button>
            ) : null}
          </div>

          <aside className={styles.benefitsNotice}>
            <strong className={styles.benefitsTitle}>{copy.benefitsTitle}</strong>
            <p>{copy.benefitsContent}</p>
          </aside>
        </section>
      </DialogContent>
    </Dialog>
  );
}

export default TwoConfirmTeamOrder;
